import { useState, useEffect } from "react";
import { Settings2, Bell, Volume2, Play, Square, Repeat } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { RINGTONE_OPTIONS, readSoundPrefs, writeSoundPrefs } from "../hooks/useWebSocket";
import { readLoopPref, writeLoopPref } from "../lib/bookingAlertManager";
import { toast } from "sonner";

export default function NotificationSoundSettings({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [ringtone, setRingtone] = useState("bell");
  const [volume, setVolume] = useState(0.8);
  const [loopUntilAck, setLoopUntilAck] = useState(true);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    const p = readSoundPrefs();
    setRingtone(p.ringtone);
    setVolume(p.volume);
    setLoopUntilAck(readLoopPref());
  }, [open]);

  const persist = (next) => {
    writeSoundPrefs(next);
    toast.success("Préférences enregistrées");
  };

  const handleRingtoneChange = (id) => {
    setRingtone(id);
    persist({ ringtone: id, volume });
  };

  const handleVolumeCommit = (vals) => {
    const v = (vals?.[0] ?? 80) / 100;
    setVolume(v);
    persist({ ringtone, volume: v });
  };

  const handleLoopToggle = (checked) => {
    setLoopUntilAck(checked);
    writeLoopPref(checked);
    toast.success(checked ? "Boucle activée" : "Boucle désactivée");
  };

  const startPreview = () => {
    if (previewing) {
      window.__trigoStopBookingRingtone?.();
      setPreviewing(false);
      return;
    }
    // Loop the selected ringtone for ~8s (same logic as a real booking alert)
    window.__trigoPlayBookingRingtone?.(ringtone, volume);
    setPreviewing(true);
    setTimeout(() => setPreviewing(false), 8200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) window.__trigoStopBookingRingtone?.(); }}>
      <DialogTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/20 text-xs font-medium text-white/80 hover:bg-white/10 hover:text-white transition-all`}
          data-testid="notification-sound-settings-btn"
          title="Préférences de sonnerie"
          aria-label="Préférences de sonnerie"
        >
          <Settings2 className="w-3.5 h-3.5" />
          {!compact && <span className="hidden sm:inline">Sonnerie</span>}
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md" data-testid="notification-sound-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
            <Bell className="w-5 h-5 text-[#FACC15]" />
            Préférences sonnerie
          </DialogTitle>
          <DialogDescription>
            Choisissez la sonnerie d&apos;alerte pour les nouvelles réservations et réglez son volume.
          </DialogDescription>
        </DialogHeader>

        {/* Ringtone choice */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide">Type de sonnerie</p>
          <div className="grid gap-2">
            {RINGTONE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleRingtoneChange(opt.id)}
                data-testid={`ringtone-option-${opt.id}`}
                className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                  ringtone === opt.id
                    ? "border-[#FACC15] bg-[#FACC15]/10 text-[#0A2540] font-semibold"
                    : "border-[#CBD5E1] text-[#475569] hover:bg-[#F8FAFC]"
                }`}
              >
                <span>{opt.label}</span>
                {ringtone === opt.id && (
                  <span className="text-[10px] uppercase font-bold tracking-wider text-[#0A2540] bg-[#FACC15] px-2 py-0.5 rounded-full">
                    Actif
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Volume slider */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide flex items-center gap-1.5">
              <Volume2 className="w-3.5 h-3.5" />
              Volume
            </p>
            <span className="text-xs text-[#64748B]" data-testid="ringtone-volume-value">
              {Math.round(volume * 100)}%
            </span>
          </div>
          <Slider
            value={[Math.round(volume * 100)]}
            onValueChange={(v) => setVolume((v?.[0] ?? 80) / 100)}
            onValueCommit={handleVolumeCommit}
            min={0}
            max={100}
            step={5}
            data-testid="ringtone-volume-slider"
          />
        </div>

        {/* Loop until acknowledged toggle */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-[#CBD5E1] bg-[#F8FAFC]">
            <div className="flex items-start gap-2 min-w-0">
              <Repeat className="w-4 h-4 text-[#FACC15] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <Label htmlFor="loop-until-ack-switch" className="text-sm font-semibold text-[#0A2540] cursor-pointer">
                  Sonner en boucle jusqu&apos;à acceptation
                </Label>
                <p className="text-[11px] text-[#64748B] leading-snug">
                  La sonnerie répète toutes les 10&nbsp;s tant que vous n&apos;avez pas accepté ou refusé la course.
                </p>
              </div>
            </div>
            <Switch
              id="loop-until-ack-switch"
              checked={loopUntilAck}
              onCheckedChange={handleLoopToggle}
              data-testid="loop-until-ack-switch"
            />
          </div>
        </div>

        {/* Test button */}
        <div className="pt-2 flex items-center justify-between gap-2 border-t border-[#E2E8F0] mt-2">
          <p className="text-xs text-[#64748B]">
            Aperçu de 8&nbsp;secondes max (durée d&apos;une vraie alerte)
          </p>
          <Button
            type="button"
            onClick={startPreview}
            data-testid="ringtone-test-btn"
            className={previewing ? "bg-[#EF4444] hover:bg-red-600 text-white" : "bg-[#0A2540] hover:bg-[#0f365c] text-white"}
            size="sm"
          >
            {previewing ? (
              <>
                <Square className="w-3.5 h-3.5 mr-1.5" /> Arrêter
              </>
            ) : (
              <>
                <Play className="w-3.5 h-3.5 mr-1.5" /> Tester
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
